import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import {useLocalStorage} from "../hooks/useLocalStorage";
import {LucideExternalLink} from "lucide-react";

const TableContext = createContext<TableContextData>(null!)

interface TableContextData{
    sorting: string | undefined
    setSorting: (s: string | undefined) => void
    compact: boolean
    defaultSorting?: string
}

export function Table(props: {children: React.ReactNode, defaultSortKey?: string, onScrollBottom?: () => void}) {

    const [sorting, setSorting] = useState<string>()
    const [displayMode, ] = useLocalStorage("displayMode")
    const compact = displayMode === "compact"

    const ctx: TableContextData = {
        sorting,
        setSorting,
        compact,
        defaultSorting: props.defaultSortKey
    }

    const bottomScrollCallback = props.onScrollBottom;

    // Table container ref for scroll detection
    const tableContainerRef = useRef<HTMLDivElement>(null)

    // Attach scroll listener
    useEffect(() => {
        const container = tableContainerRef.current
        if (!container || !bottomScrollCallback) return

        const handleScroll = () => {
            const container = tableContainerRef.current
            if (!container) return

            const { scrollTop, scrollHeight, clientHeight } = container
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100

            if (isNearBottom) {
                bottomScrollCallback()
            }
        }

        container.addEventListener('scroll', handleScroll)
        return () => container.removeEventListener('scroll', handleScroll)

    }, [bottomScrollCallback])

    return <div ref={tableContainerRef} className="overflow-auto rounded-md rst-scrollbar rst-scrollbar-track border border-neutral-500">
        <table className={`w-full text-left border-collapse bg-neutral-900 [&_td]:px-2 [&_th]:px-2 [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap ${ctx.compact ? "[&_td]:py-1 [&_th]:py-1" : "[&_td]:py-2 [&_th]:py-2"}`}>
            <TableContext.Provider value={ctx}>
                {props.children}
            </TableContext.Provider>
        </table>
    </div>
}

export function HeaderRow(props: {children: React.ReactNode}) {

    return <thead className={`bg-neutral-800 sticky top-0`}>
        <tr className={`text-left select-none `} style={{boxShadow: '0 1px 0 rgb(64 64 64)'}}>
            {props.children}
        </tr>
    </thead>
}

export function HeaderCell(props: {children?: React.ReactNode, sortKey?: string}) {
    const ctx = useContext(TableContext)

    if (props.sortKey){
        const icon = ctx.sorting === props.sortKey ? "▲" : ctx.sorting === `-${props.sortKey}` ? "▼" : undefined

        const updateSorting = () => {
            if (ctx.sorting === props.sortKey) {
                ctx.setSorting(undefined)
            } else if (ctx.sorting === `-${props.sortKey}`) {
                ctx.setSorting(props.sortKey)
            } else {
                ctx.setSorting(`-${props.sortKey}`)
            }
        }

        return <th className={`cursor-pointer select-none`} onClick={updateSorting}>
            {props.children}{icon && <>&nbsp;{icon}</>}
        </th>
    }
    else{
        return <th>{props.children}</th>
    }
}

export function BodyRowMapper<T extends {[key: string]: any}>(props: {data?: T[], map: (data: T, compact: boolean) => React.ReactNode, placeholder?: string, hideEmpty?: boolean}) {
    const ctx = useContext(TableContext)

    function sort(a: T, b: T): number{
        if (!ctx.sorting && !ctx.defaultSorting){
            return 0
        }
        let sorting = ctx.sorting ?? ctx.defaultSorting!
        let m = 1
        if (sorting.startsWith('-')){
            sorting = sorting.substring(1)
            m = -1
        }
        const numA = Number(a[sorting])
        const numB = Number(b[sorting])
        if (!isNaN(numA) && !isNaN(numB)){
            return (numA - numB) * m
        }
        if (a[sorting] > b[sorting]){
            return m
        }
        if (a[sorting] < b[sorting]){
            return -1 * m
        }
        return 0
    }

    return <tbody className={`select-text overflow-y-auto`}>
        {props.data && [...props.data].sort(sort).map(d => props.map(d, ctx.compact))}
        {(!props.data || props.data.length < 1) && !props.hideEmpty && <tr>
            <td className="text-center text-neutral-400 p-3 italic select-none" colSpan={1000}>{props.placeholder ?? "No data"}</td>
        </tr>}
    </tbody>
}

interface BodyRowProps {
    highlight?: boolean
    children?: React.ReactNode
    onClick?: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void
    onContextMenu?: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void
}

export function BodyRow(props: BodyRowProps) {

    return <tr onContextMenu={props.onContextMenu} onClick={props.onClick}
               className={`text-left border-neutral-700 border-b last:border-none select-none cursor-default ${props.highlight === undefined ? "hover:bg-neutral-800" : props.highlight ? "bg-neutral-800" : ""}`}>
        {props.children}
    </tr>
}

interface BodyCellProps{
    children?: React.ReactNode
    onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

export function BodyCell(props: BodyCellProps) {
    const ctx = useContext(TableContext)

    function onClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>){
        if (props.onClick) {
            e.stopPropagation()
            props.onClick(e)
        }
    }

    if (props.onClick){
        return <td>
            <div className="flex flex-row items-center">
                <button onClick={onClick} className={"ms-1 text-neutral-300 hover:text-white rounded-md " + (ctx.compact ? "p-1" : "p-2")}>
                    <LucideExternalLink size={14}/>
                </button>
                {props.children}
            </div>
        </td>
    }
    else{
        return <td>{props.children}</td>
    }

}